import React, { useMemo } from "react";
import Visibility from "../utils/Visibility";
import { AddToCart, CalcQuantity } from "../utils/CartFunctions";
import { Button } from "../ui/button";
import Spacer from "../Spacer";
import { Skeleton } from "../ui/skeleton";
import { SheetClose } from "../ui/sheet";
import { toast } from "react-toastify";
import { CheckCircle2 } from "lucide-react";
import ProductModel from "../../Models/PorductModel";
import UAEDirhamIcon from "../utils/UAEDirhamIcon";

// ============================================================================
// Types
// ============================================================================

type ProductTagsProps = {
  product: ProductModel;
  t: (key: string) => string;
};

type ProductCartElementProps = {
  product: ProductModel;
  t: (key: string) => string;
  navigate: (path: string) => void;
  params: { branch_id?: string; domain?: string };
  optionsSelected: any[];
  itemLoading: boolean;
  productSheet: boolean;
};

type ProductDetailsProps = {
  product: ProductModel;
  name: string;
  description: string;
};

// ============================================================================
// ProductTags Component
// ============================================================================

export function ProductTags({ product, t }: ProductTagsProps) {
  const hasTags = product.new || product.unavailable || product.popular;
  const tagBaseClass = "text-sm px-2 py-1 bg-primary text-onPrimary rounded-md";

  if (!hasTags) return null;

  return (
    <div className="flex gap-1 flex-wrap">
      <Visibility visible={!!product.new}>
        <span className={tagBaseClass}>{t("gloabl.new")}</span>
      </Visibility>
      <Visibility visible={!!product.popular}>
        <span className={tagBaseClass}>{t("gloabl.common")}</span>
      </Visibility>

    </div>
  );
}

// ============================================================================
// Helper Functions
// ============================================================================

const handleAddToCart = (
  product: ProductModel,
  optionsSelected: any[],
  note: string,
  quantity: number,
  params: { branch_id?: string; domain?: string },
  navigate: (path: string) => void,
  t: (key: string) => string
) => {
  AddToCart({
    element: { ...product, option: optionsSelected, note },
    params,
    navigate,
    t,
    quantity,
  });

  if (params.branch_id) {
    CalcQuantity(Number(params.branch_id));
  }

  toast.success(
    <div className="flex items-center gap-3">
      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
      <span className="font-semibold text-primary">{t("gloabl.addedToCart")}</span>
    </div>,
    {
      className: "!bg-card !border-2 !border-primary/50 !rounded-xl !shadow-xl !backdrop-blur-sm",
      bodyClassName: "!text-primary !font-semibold",
      progressClassName: "!bg-primary",
      hideProgressBar: false,
      onClick: () => {
        navigate(`/branches/${params.branch_id}/cart`);
      },
    }
  );
};

// ============================================================================
// ProductCartElement Component
// ============================================================================

export function ProductCartElement({
  product,
  t,
  navigate,
  params,
  optionsSelected,
  itemLoading,
  productSheet,
}: ProductCartElementProps) {
  const [quantity, setQuantity] = React.useState(1);
  const [note, setNote] = React.useState("");

  const handleQuantityDecrease = () => setQuantity((q) => Math.max(1, q - 1));
  const handleQuantityIncrease = () => setQuantity((q) => q + 1);

  const handleAddToCartClick = () => {
    handleAddToCart(product, optionsSelected, note, quantity, params, navigate, t);
  };

  // حساب المجموع الكلي: (سعر المنتج + أسعار الخيارات) × الكمية
  const totalPrice = useMemo(() => {
    const basePrice = product.price_offer || product.price;
    let optionsPrice = 0;
    
    if (optionsSelected && optionsSelected.length > 0) {
      optionsSelected.forEach((opt: any) => {
        if (opt.subOption && opt.subOption.price) {
          optionsPrice += opt.subOption.price;
        }
      });
    }
    
    return (basePrice + optionsPrice) * quantity;
  }, [product.price, product.price_offer, optionsSelected, quantity]);

  const buttonQuantityClass = "bg-primary text-onPrimary px-4 py-2 rounded-lg text-lg";
  const buttonAddToCartClass =
    "text-onPrimary bg-primary rounded-xl hover:bg-primary/90 focus:ring-4 focus:outline-none focus:ring-primary/50 font-medium text-sm px-5 py-2.5 text-center";

  if (itemLoading) {
    return <RenderLoadingCartElement />;
  }

  if (product.unavailable) {
    return (
      <div className="flex flex-col gap-2">
        <div
          className="w-full text-center bg-background/70 border border-border rounded-xl px-5 py-3 text-sm font-semibold text-muted-foreground cursor-not-allowed"
          aria-disabled="true"
        >
         {t("gloabl.unavailable")}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Quantity Selector */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">{t("gloabl.quantity")}</h2>
          <div className="flex items-center gap-4">
            <Button onClick={handleQuantityDecrease} className={buttonQuantityClass}>
              -
            </Button>
            <span className="text-base">{quantity}</span>
            <Button onClick={handleQuantityIncrease} className={buttonQuantityClass}>
              +
            </Button>
          </div>
        </div>
      </div>

      {/* Note Textarea */}
      <textarea
        className="w-full border-2 border-gray-300 rounded-md p-2"
        placeholder={t("gloabl.addNote")}
        name="note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      <Spacer height={4} />

      {/* Add to Cart Button */}
      {productSheet ? (
        <SheetClose asChild>
          <Button onClick={handleAddToCartClick} className={buttonAddToCartClass}>
            <span className="flex items-center gap-2">
              {t("gloabl.AddToCart")}
              <span className="flex items-center gap-1">
                (<UAEDirhamIcon className="w-3 h-3" />
                {totalPrice.toFixed(1)})
              </span>
            </span>
          </Button>
        </SheetClose>
      ) : (
        <Button onClick={handleAddToCartClick} className={buttonAddToCartClass}>
          <span className="flex items-center gap-2">
            {t("gloabl.AddToCart")}
            <span className="flex items-center gap-1">
              (<UAEDirhamIcon className="w-3 h-3" />
              {totalPrice.toFixed(1)})
            </span>
          </span>
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// ProductDetails Component
// ============================================================================

export function ProductDetails({ product, name, description }: ProductDetailsProps) {
  const formattedDescription = product[description as keyof ProductModel]
    ?.toString()
    ?.replace(/\n/g, "<br/>") || "";

  const hasPriceOffer = product.price_offer > 0;

  return (
    <div className="space-y-3">
      {/* Description */}
      {formattedDescription && (
        <p
          className="text-[0.9rem] font-semibold line-clamp-3"
          dangerouslySetInnerHTML={{ __html: formattedDescription }}
        />
      )}

      {/* Price Section */}
      <div className="text-base font-semibold flex justify-end gap-[65%]">
        <p
          className={`flex items-center gap-1 ${hasPriceOffer
              ? "line-through decoration-primary decoration-solid decoration-2 text-foreground/50"
              : ""
            }`}
        >
          <UAEDirhamIcon className="w-4 h-4" />
          {product.price}
        </p>
        <Visibility visible={hasPriceOffer}>
          <p className="flex items-center gap-1 font-bold text-primary">
            <UAEDirhamIcon className="w-4 h-4" />
            {product.price_offer}
          </p>
        </Visibility>
      </div>
    </div>
  );
}

// ============================================================================
// Loading Components
// ============================================================================

const RenderLoadingCartElement = () => {
  return (
    <div className="flex flex-col gap-2 px-5">
      <div className="space-y-2 flex justify-between items-center">
        <Skeleton className="w-20 h-7" />
        <div className="flex items-center gap-4">
          <Skeleton className="w-9 h-9" />
          <Skeleton className="w-7 h-7" />
          <Skeleton className="w-9 h-9" />
        </div>
      </div>
      <Skeleton className="w-full h-20 rounded-md p-2" />
      <Spacer height={4} />
      <Skeleton className="w-full h-9" />
    </div>
  );
};
